import { type KeyboardEvent, type MouseEvent, useId, useState } from 'react';
import {
  normalizeProjectBadgeChars,
  projectBadgeColorOptions,
  resolveProjectBadgeSwatch,
} from '@/shared/data/projectBadges';
import { r, s } from '@/theme/tokens';
import { Box } from '@/ui';
import { Checkbox, FormHelperText, Menu, MenuItem, Text, TextField } from '@/ui/primitives';
import { formFieldLabelSx } from '@/ui/primitives/formFieldStyles';
import type { ProjectBadgeColor } from '@flowqa/shared';
import type { ReactElement } from 'react';

type ProjectBadgeFieldProps = {
  enabled: boolean;
  onEnabledChange: (value: boolean) => void;
  chars: string;
  color: ProjectBadgeColor;
  onCharsChange: (value: string) => void;
  onColorChange: (value: ProjectBadgeColor) => void;
  disabled?: boolean;
};

const swatchSize = '18px';
const swatchRadius = '6px';
const controlSize = '32px';
const colorSelectWidth = controlSize;
const checkboxColumnWidth = controlSize;

const swatchSx = {
  display: 'inline-flex',
  width: swatchSize,
  height: swatchSize,
  minWidth: swatchSize,
  minHeight: swatchSize,
  maxWidth: swatchSize,
  maxHeight: swatchSize,
  flex: '0 0 auto',
  boxSizing: 'border-box',
  borderRadius: swatchRadius,
  border: '1px solid',
  borderColor: 'divider',
} as const;

const projectBadgeHelperTextSx = {
  mt: s('xxs'),
  fontSize: '13px',
} as const;

export const ProjectBadgeField = ({
  enabled,
  onEnabledChange,
  chars,
  color,
  onCharsChange,
  onColorChange,
  disabled = false,
}: ProjectBadgeFieldProps): ReactElement => {
  const fieldId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const controlsDisabled = disabled || !enabled;

  const handleOpenColorMenu = (event: MouseEvent<HTMLElement>): void => {
    if (controlsDisabled) return;
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseColorMenu = (): void => {
    setAnchorEl(null);
  };

  const handleColorKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (controlsDisabled) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `minmax(0, 1fr) ${colorSelectWidth} ${checkboxColumnWidth}`,
        alignItems: 'start',
        columnGap: s('md'),
        rowGap: s('sm'),
        width: '100%',
      }}
    >
      <Text role="body" component="label" htmlFor={fieldId} sx={formFieldLabelSx}>
        Project Badge
      </Text>
      <Box />
      <Box />
      <TextField
        id={fieldId}
        fieldStyle="settings"
        inputHeight={32}
        value={chars}
        placeholder="PR"
        slotProps={{ htmlInput: { maxLength: 2 } }}
        disabled={controlsDisabled}
        onChange={(event) => {
          onCharsChange(normalizeProjectBadgeChars(event.target.value));
        }}
      />
      <Box
        role="button"
        tabIndex={controlsDisabled ? -1 : 0}
        aria-label="Choose project badge color"
        onClick={handleOpenColorMenu}
        onKeyDown={handleColorKeyDown}
        sx={{
          minWidth: colorSelectWidth,
          width: colorSelectWidth,
          maxWidth: colorSelectWidth,
          minHeight: controlSize,
          height: controlSize,
          maxHeight: controlSize,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: r('md'),
          backgroundColor: 'background.default',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: controlsDisabled ? 'default' : 'pointer',
          opacity: controlsDisabled ? 0.6 : 1,
        }}
      >
        <Box
          component="span"
          sx={[swatchSx, { backgroundColor: resolveProjectBadgeSwatch(color) }]}
        />
      </Box>
      <Box
        sx={{
          width: checkboxColumnWidth,
          minWidth: checkboxColumnWidth,
          maxWidth: checkboxColumnWidth,
          height: controlSize,
          minHeight: controlSize,
          maxHeight: controlSize,
          display: 'grid',
          placeItems: 'center',
          // mx: `-${s('sm')}`,
        }}
      >
        <Checkbox
          checked={enabled}
          disabled={disabled}
          onChange={(event) => {
            onEnabledChange(event.target.checked);
          }}
          slotProps={{ input: { 'aria-label': 'Enable project badge' } }}
          sx={{
            p: s('none'),
            '& .MuiSvgIcon-root': {
              fontSize: '24px',
            },
          }}
        />
      </Box>
      <FormHelperText sx={projectBadgeHelperTextSx}>Up to 2 characters.</FormHelperText>
      <Box />
      <Box />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseColorMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: colorSelectWidth,
              width: colorSelectWidth,
              maxWidth: colorSelectWidth,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: `${r('md')} !important`,
              backgroundColor: 'background.default',
              backgroundImage: 'none',
              boxShadow: 'none',
              overflow: 'hidden',
            },
          },
          list: {
            sx: {
              p: s('none'),
              m: s('none'),
              backgroundColor: 'background.default',
            },
          },
        }}
      >
        {projectBadgeColorOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => {
              onColorChange(option.value);
              handleCloseColorMenu();
            }}
            sx={{
              minHeight: controlSize,
              px: s('md'),
              py: s('xs'),
              justifyContent: 'center',
              borderRadius: r('none'),
              backgroundColor: color === option.value ? 'action.selected' : undefined,
            }}
          >
            <Box component="span" sx={[swatchSx, { backgroundColor: option.swatch }]} />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
